/**
 * lib/publicHolidays.ts
 * ------------------------------------------------------------------
 * Live public-holidays store (Stream H, item B / LKC Item 1).
 *
 * Holidays move from the hardcoded `lib/holidays.ts` list into a
 * Firestore collection `publicHolidays` (doc id = 'YYYY-MM-DD') so the
 * office can set next year's gazette dates and add ad-hoc mid-year
 * holidays without a code change. The static `HOLIDAYS_2026` list
 * stays as a FALLBACK: until the collection is seeded (or if it ever
 * reads empty), the hook serves the static list, so nothing breaks.
 *
 * The leave-calculation rules (next item) build on this: a paid public
 * holiday inside a leave span does not consume leave.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import { db, setDoc } from '@/lib/firebase';
import { collection, doc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { HOLIDAYS_2026, type Holiday } from '@/lib/holidays';

export const PUBLIC_HOLIDAYS_COLLECTION = 'publicHolidays';

export type { Holiday };

const byDateAsc = (a: Holiday, b: Holiday) => a.date.localeCompare(b.date);
const fallback = (): Holiday[] => [...HOLIDAYS_2026].sort(byDateAsc);

export interface UsePublicHolidays {
  /** Live list (sorted), or the static fallback until the collection is seeded. */
  holidays: Holiday[];
  /** date 'YYYY-MM-DD' -> name. */
  byDate: Map<string, string>;
  loading: boolean;
  /** True once the Firestore collection has at least one doc. */
  isLive: boolean;
  isHoliday: (dateStr: string) => boolean;
  holidayName: (dateStr: string) => string | undefined;
}

export function usePublicHolidays(): UsePublicHolidays {
  const [holidays, setHolidays] = useState<Holiday[]>(fallback);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, PUBLIC_HOLIDAYS_COLLECTION),
      (snap) => {
        if (snap.empty) {
          setHolidays(fallback());
          setIsLive(false);
        } else {
          const list: Holiday[] = snap.docs.map((d) => ({
            date: d.id,
            name: (d.data().name as string) || 'Public Holiday',
          }));
          list.sort(byDateAsc);
          setHolidays(list);
          setIsLive(true);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, []);

  return useMemo(() => {
    const byDate = new Map(holidays.map((h) => [h.date, h.name]));
    return {
      holidays,
      byDate,
      loading,
      isLive,
      isHoliday: (dateStr: string) => byDate.has(dateStr),
      holidayName: (dateStr: string) => byDate.get(dateStr),
    };
  }, [holidays, loading, isLive]);
}

/** Add or rename a public holiday (doc id = date). */
export async function savePublicHoliday(date: string, name: string): Promise<void> {
  await setDoc(
    doc(db, PUBLIC_HOLIDAYS_COLLECTION, date),
    { date, name: name.trim() || 'Public Holiday', updatedAt: new Date().toISOString() },
    { merge: true },
  );
}

/** Remove a public holiday. */
export async function deletePublicHoliday(date: string): Promise<void> {
  await deleteDoc(doc(db, PUBLIC_HOLIDAYS_COLLECTION, date));
}
